import {
  ChangeEvent,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  DialogContent as ReachDialogContent,
  DialogOverlay as ReachDialogOverlay,
} from "@reach/dialog";
import {
  Container,
  Flex,
  Input,
  Link,
  PrimaryButton,
  system,
  SystemProps,
  Text, LegacyText,
} from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import { FixedSizeImage } from "~components/common/Image";

import { useRouter } from "next/router";
import { SlateRenderer } from "~components";
import { BaseContext } from "~context";
import { GetEventAndReleaseForCustomerQuery } from "~graphql/sdk";
import { getGraphQLError, handlePromise, sdk, showToast } from "~lib";
import { flicketLogo } from "~lib/helpers/constants";

const MotionOverlay = motion(ReachDialogOverlay);

const MotionContent = motion(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)`
  background-color: ${(p) => p.theme.colors.N100};

  display: flex;
  justify-content: center;
  align-items: center;

  position: fixed;
  top: 0;

  width: 100%;
  height: 100%;

  z-index: 1501;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    overflow: auto;

    padding-top: ${(p) => p.theme.space[4]}px;
    padding-bottom: ${(p) => p.theme.space[4]}px;
  }
`;

const DialogContent = styled(MotionContent)<SystemProps>`
  height: 100%;

  width: 100%;
  max-width: 442px;

  background-color: ${(p) => p.theme.colors.N100};

  overflow: auto;
  display: flex;
  flex-direction: column;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    overflow: visible;
    height: auto;
    max-height: 100%;
    background-color: ${(p) => p.theme.colors.transparent};
  }

  ${system}
`;

type ModalProps = SystemProps & {
  isOpen: boolean;
  onClose?: () => void;
};

const CustomModal = ({
  isOpen,
  onClose,
  children,
  ...props
}: PropsWithChildren<ModalProps>) => {
  return (
    <AnimatePresence>
      <DialogOverlay isOpen={isOpen} onDismiss={onClose}>
        {/* @ts-expect-error fix this */}
        <DialogContent
          aria-label={"dialog content"}
          initial={{
            y: -100,
          }}
          animate={{
            y: 0,
          }}
          exit={{
            y: 100,
          }}
          {...props}
        >
          {children}
        </DialogContent>
      </DialogOverlay>
    </AnimatePresence>
  );
};

type AccessModalProps = {
  release: GetEventAndReleaseForCustomerQuery["getEventAndReleaseForCustomer"]["release"];
  setHasAccess: (hasAccess: boolean) => void;
};

export const AccessModal = ({ release, setHasAccess }: AccessModalProps) => {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { organization } = useContext(BaseContext);
  const [autoValidating, setAutoValidating] = useState(!!router?.query.code);

  useEffect(() => {
    sessionStorage.removeItem("releasePasswordId");
  }, []);

  const validatePassword = async (password) => {
    setIsSubmitting(true);

    if (!password) {
      showToast("A code is required");
      return setIsSubmitting(false);
    }

    const passwordLookup = await handlePromise(async () =>
      sdk({ orgId: organization?.id }).validateReleasePassword({
        id: release.id,
        password,
      })
    );

    if (passwordLookup.error) {
      let error = getGraphQLError(passwordLookup.error);

      // if the password is not found in the current release, then we need to check if the password is valid for another release
      if (error.statusCode === 404) {
        const releaseLookup = await handlePromise(async () =>
          sdk({ orgId: organization?.id }).validateReleasePasswordOrRelease({
            eventId: release.event.id,
            password,
          })
        );
        const newSlug =
          releaseLookup.data?.validateReleasePasswordOrRelease?.slug;
        if (newSlug) {
          // if the password is valid for another release, then we can redirect to that release
          return router.push(
            `/events/${release.event.id}/reservation?slug=${newSlug}&code=${password}`
          );
        }
        error = getGraphQLError(releaseLookup.error);
      }
      showToast(error.message, "error");
      return setIsSubmitting(false);
    }

    sessionStorage.setItem(
      "releasePasswordId",
      passwordLookup.data.validateReleasePassword.id
    );
    setHasAccess(true);

    // if quantity is -1, then the release is unlimited so we can skip the check
    if (passwordLookup.data.validateReleasePassword.quantityPaid === -1) {
      return setIsSubmitting(true);
    }

    // These orgs don't want the remaining qty alert to show
    if (
      [
        // HTX Ultimate
        "643a9b0b-2e21-4a2c-a6bf-2d9f0f37cd95",
        // Notion Touring
        "9c1fadb1-853b-4344-9dcf-e994ac9166be",
        // National Rugby League
        "9cdb2867-c28b-47f2-b474-65cb9c99c212",
      ].includes(organization?.id)
    ) {
      return setIsSubmitting(true);
    }

    const compTicketsRemaining =
      passwordLookup.data.validateReleasePassword.quantityComp -
      passwordLookup.data.validateReleasePassword.totalUsedComp;

    const paidTicketsRemaining =
      passwordLookup.data.validateReleasePassword.quantityPaid -
      passwordLookup.data.validateReleasePassword.totalUsedPaid;

    if (compTicketsRemaining >= 10 || paidTicketsRemaining >= 10) {
      return setIsSubmitting(true);
    }

    window.alert(
      `${
        compTicketsRemaining > 0
          ? `You can access ${compTicketsRemaining} complimentary ${
              compTicketsRemaining > 1 ? "tickets. " : "ticket. "
            }`
          : ""
      } ${
        paidTicketsRemaining > 0
          ? `You can purchase ${paidTicketsRemaining} ${
              paidTicketsRemaining > 1 ? "tickets. " : "ticket. "
            }`
          : ""
      }`
    );

    return setIsSubmitting(true);
  };

  useEffect(() => {
    if (router?.query.code) {
      const getAccess = async () => {
        const decoded = decodeURIComponent(router?.query.code as string);
        setPassword(decoded);
        await validatePassword(decoded);
        setAutoValidating(false);
      };
      void getAccess();
    }
  }, [router?.query.code]);

  return (
    <CustomModal isOpen={!autoValidating}>
      <Flex justifyContent="center" mb={{ _: 4, md: 2 }} mt={{ _: 8, md: 0 }}>
        <Link href="/">
          <FixedSizeImage
            image={organization?.branding?.logo}
            fallback={flicketLogo}
            alt={`${organization?.name} logo`}
            width={80}
            height={30}
          />
        </Link>
      </Flex>
      <Container innerProps={{ p: { _: 2, md: 3 } }}>
        <form
          onSubmit={() => {
            void validatePassword(password);
          }}
        >
          <LegacyText
            fontWeight="heavy"
            fontSize={6}
            color="N800"
            lineHeight="normal"
            textAlign="center"
            mb={2}
          >
            Access release
          </LegacyText>
          {!!release?.presaleLogo && (
            <Flex mb={3} justifyContent="center">
              <FixedSizeImage
                image={release?.presaleLogo}
                fallback={flicketLogo}
                alt={`${organization?.name} presale logo`}
                width={120}
                height={30}
              />
            </Flex>
          )}
          <LegacyText
            fontWeight="heavy"
            fontSize={4}
            color="N800"
            lineHeight="normal"
            mb={2}
          >
            {release?.presaleTitle ?? release?.event?.title}
          </LegacyText>

          {release?.presaleInformation && (
            <SlateRenderer value={release.presaleInformation} />
          )}

          <Input
            mt={4}
            mb={4}
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
          />
          <PrimaryButton
            w="100%"
            disabled={!password}
            isLoading={isSubmitting}
            onClick={() => {
              void validatePassword(password);
            }}
            type="submit"
          >
            Enter
          </PrimaryButton>
        </form>
      </Container>
    </CustomModal>
  );
};
